import { useLanguage } from "@/lib/LanguageContext";
import { ArrowRight, ArrowDown } from "lucide-react";

export default function Hero() {
  const { t } = useLanguage();

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <section
      data-testid="hero-section"
      className="relative min-h-screen flex items-end overflow-hidden grain"
    >
      {/* Background image */}
      <div className="absolute inset-0">
        <img
          src="/img/workshop-hero.jpg"
          alt="Werkstatt für Camperausbau und Reisemobil-Service in Volkertshausen"
          loading="eager"
          fetchpriority="high"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0B0C10] via-[#0B0C10]/85 to-[#0B0C10]/40" />
        <div className="absolute inset-0 bg-[#0B0C10]/30" />
      </div>

      {/* Content */}
      <div className="relative w-full px-6 sm:px-12 lg:px-24 pb-20 lg:pb-28 pt-32">
        <div className="max-w-5xl">
          <p
            data-testid="hero-overline"
            className="text-xs font-bold uppercase tracking-[0.3em] text-[#D97736] mb-8 reveal in-view"
          >
            {t.hero.overline}
          </p>

          <h1
            data-testid="hero-title"
            className="font-display font-black text-white text-5xl sm:text-7xl lg:text-[8rem] xl:text-[9rem] leading-[0.95] tracking-tighter mb-8"
          >
            <span className="block reveal in-view">{t.hero.title_l1}</span>
            <span
              className="block reveal in-view"
              style={{ animationDelay: "0.15s" }}
            >
              {t.hero.title_l2}
              <span className="text-[#D97736]">.</span>
            </span>
          </h1>

          <p
            data-testid="hero-subtitle"
            className="text-base sm:text-lg text-[#A0AAB2] max-w-2xl leading-relaxed mb-10 reveal in-view"
            style={{ animationDelay: "0.3s" }}
          >
            {t.hero.subtitle}
          </p>

          <div
            className="flex flex-col sm:flex-row gap-4 reveal in-view"
            style={{ animationDelay: "0.45s" }}
          >
            <button
              data-testid="hero-cta-primary"
              onClick={() => scrollTo("contact")}
              className="group bg-[#D97736] hover:bg-[#C2662B] text-white px-8 py-5 font-bold tracking-wider uppercase text-sm inline-flex items-center justify-center gap-3 transition-colors"
            >
              {t.hero.cta_primary}
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </button>
            <button
              data-testid="hero-cta-secondary"
              onClick={() => scrollTo("services")}
              className="group border border-white/30 hover:border-white text-white px-8 py-5 font-bold tracking-wider uppercase text-sm inline-flex items-center justify-center gap-3 transition-colors bg-white/0 hover:bg-white/5"
            >
              {t.hero.cta_secondary}
              <ArrowDown className="w-4 h-4 group-hover:translate-y-1 transition-transform" />
            </button>
          </div>

          {/* Stats strip */}
          <div
            className="mt-20 grid grid-cols-3 gap-6 sm:gap-12 max-w-2xl border-t border-white/10 pt-10 reveal in-view"
            style={{ animationDelay: "0.6s" }}
          >
            {t.hero.stats.map((s, i) => (
              <div key={i} data-testid={`hero-stat-${i}`}>
                <div className="font-display font-black text-3xl sm:text-5xl text-white tracking-tight">
                  {s.value}
                </div>
                <div className="text-xs uppercase tracking-widest text-[#A0AAB2] mt-2">
                  {s.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
